import { useMemo } from 'react';

import { useDs2ExperimentContext } from './context';

type KnownVariations = 'control' | 'treatment';

type Result = {
	/**
	 * `true` if experiment treated the user to the `'treatment'` variation.
	 */
	isTreatment: boolean;
	/**
	 * The actual variation string. Currently limited to 'treatment' or 'control', with 'control' being the default if
	 * the experiment context was not configured.
	 */
	variation: KnownVariations;
	/**
	 * `true` if the FCM is enabled and defines the experiment. This ignores whether the user was treated into the experiment. This can be used as a "kill switch" for whether DS2 should be used at all.
	 */
	fcmEnabled: boolean;
};

const identifyKnownVariation = (rawVariation?: string): KnownVariations =>
	rawVariation === 'treatment' ? 'treatment' : 'control';

/**
 * This hook can be used in shared code to decide whether `@change/ds` components can be shown.
 *
 * If this hook is called within a page that is not set up to fetch & provide the experiment state, it will return the
 * default experiment state state: `{ isEnabled: false, variation: 'control' }`. This is by design because pages to have
 * to opt-in to DS2 (by including the required providers). But we want to allow shared code to check for this experiment
 * without worrying about whether the page has opted in yet.
 *
 * See: [README](./README.md)
 *
 * @deprecated DS2 (Tomato) has been deprecated. See the "sunrise foundations experience" FCM and experiments.
 */
export const useDs2Experiment = (): Result => {
	const { ds2FcmExperiment: { variation: rawVariation, fcmEnabled = false } = {} } = useDs2ExperimentContext() ?? {};

	const result: Result = useMemo(() => {
		const variation = identifyKnownVariation(rawVariation);
		return {
			fcmEnabled,
			isTreatment: variation === 'treatment',
			variation,
		};
	}, [rawVariation, fcmEnabled]);

	return result;
};
